import React, { forwardRef, useMemo, useRef } from "react";
import { alpha, margin } from "@mui/system";

/* types */

import { MenuList, ListItemIcon, ListSubheader, Divider, Box, Typography } from "@mzc-pdc/ui";
import { EllipsisListItemText } from "@components_v2/ellipsis-tooltip";

/* styled */
import { Popover, MenuItem } from "./styled";
import { IconButton, Button, Tooltip } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import { IconArrowDropDown, IconArrowDropUp } from "@mzc-cloudplex/icons";
import { useTranslation } from "react-i18next";

export const Menu = forwardRef(
    (
        {
            target,
            menuWidth,
            menuMargin,
            menuMinWidth,
            placement,
            type,
            startIcon,
            endIcon,
            endOpenIcon,
            label,
            color,
            size,
            variant,
            list,
            listSize,
            actions,
            divider,
            preventPropagation = true,
            tooltip,
            value,
            useInCard,
            sx = {},
            ...restProps
        },
        ref,
    ) => {
        const { t } = useTranslation();
        const [anchorEl, setAnchorEl] = React.useState(null);
        // 한 번도 열지 않은 메뉴는 팝오버 트리를 만들지 않는다 — 목록 행처럼 메뉴가 행마다 달린 화면에서 렌더 비용을 좌우한다.
        // 닫힘 트랜지션이 끝난 뒤(onExited) 해제해 닫기 애니메이션은 유지
        const [isPopoverMounted, setIsPopoverMounted] = React.useState(false);
        const testRef = useRef(null);
        const open = Boolean(anchorEl);
        const handleClick = (event) => {
            if (preventPropagation) event.stopPropagation();
            setIsPopoverMounted(true);
            setAnchorEl(event.currentTarget);
        };
        const handleClose = (event) => {
            if (preventPropagation) event.stopPropagation();
            setAnchorEl(null);
        };

        const iconButtonStyleInCard = useMemo(() => {
            if (!useInCard || type !== "icon") return { ...sx };
            return {
                ...sx,
                backgroundColor: alpha(theme.palette.constant.black, 0.6),
                color: theme.palette.constant.white,
                "&:hover": {
                    backgroundColor: alpha(theme.palette.constant.black, 0.8),
                },
                '&[aria-expanded="true"]': {
                    backgroundColor: alpha(theme.palette.constant.black, 0.92),
                },
            };
        }, [useInCard]);

        if (!list?.length) return null;

        return (
            <>
                {type === "icon" ? (
                    // 아이콘 트리거는 대부분 "더 보기" 메뉴이므로 tooltip 을 넘기지 않으면 More 로 안내한다.
                    // tooltip={null} 을 넘기면 툴팁을 노출하지 않는다.
                    <Tooltip title={tooltip === undefined ? t(`common::label::More`, `More`) : tooltip ?? ""}>
                        <IconButton
                            ref={ref}
                            id="menu-button"
                            aria-controls={open ? "menu" : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? "true" : undefined}
                            onClick={handleClick}
                            color={color}
                            size={size}
                            circled
                            active={open.toString()}
                            disabled={
                                restProps.disabled ||
                                list?.filter((item) => !item.hidden && !item.groupLabel)?.length === 0
                            }
                            sx={iconButtonStyleInCard}
                            {...restProps}
                        >
                            {label}
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Button
                        ref={ref}
                        id="menu-button"
                        aria-controls={open ? "menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={handleClick}
                        color={color}
                        size={size}
                        variant={variant}
                        startIcon={startIcon}
                        endIcon={open ? endOpenIcon : endIcon}
                        disabled={
                            restProps.disabled || list?.filter((item) => !item.hidden && !item.groupLabel)?.length === 0
                        }
                        sx={sx}
                        {...restProps}
                    >
                        {label}
                    </Button>
                )}
                {isPopoverMounted && (
                    <Popover
                        id="menu-popover"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        TransitionProps={{ onExited: () => setIsPopoverMounted(false) }}
                        anchorOrigin={(() => {
                            const placementValue = placement ?? "left";
                            if (placementValue === "top" || placementValue === "bottom") {
                                return {
                                    vertical: placementValue,
                                    horizontal: "left",
                                };
                            }
                            return {
                                vertical: "bottom",
                                horizontal: placementValue,
                            };
                        })()}
                        transformOrigin={(() => {
                            const placementValue = placement ?? "left";
                            if (placementValue === "top" || placementValue === "bottom") {
                                return {
                                    vertical: placementValue === "top" ? "bottom" : "top",
                                    horizontal: "left",
                                };
                            }
                            return {
                                vertical: "top",
                                horizontal: placementValue,
                            };
                        })()}
                        PaperProps={{
                            style: {
                                minWidth: `${menuMinWidth}px`,
                                width: `${menuWidth}px`,
                                margin: `${menuMargin}px`,
                                border: `1px solid ${theme.palette.greySecondary[100]}`,
                            },
                        }}
                    >
                        <MenuList id="menu-list">
                            {list
                                ?.filter((item) => !item.hidden)
                                .map((item, index) => {
                                    if (item.groupLabel) {
                                        return [
                                            item.divider && <Divider key={`divider-${item.value}`} />,
                                            <ListSubheader
                                                key={`group-label-${item.value}`}
                                                disableSticky
                                                sx={{
                                                    height: "34px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    px: "20px",
                                                    cursor: "default",
                                                    lineHeight: "normal",
                                                    backgroundColor: "transparent",
                                                }}
                                            >
                                                <Typography
                                                    variant={"body3_500"}
                                                    color={theme.palette.greySecondary[600]}
                                                >
                                                    {item.label}
                                                </Typography>
                                            </ListSubheader>,
                                        ];
                                    }

                                    return [
                                        item.divider && <Divider key={`divider-${item.value}`} />,
                                        <MenuItem
                                            ref={testRef}
                                            id="menu-item"
                                            key={item.value ?? item.label}
                                            disabled={item.disabled}
                                            selected={item.selected}
                                            disableGutters
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (item.onClick) item.onClick(item.value, value);
                                                if (item.clickClose) handleClose(e);
                                            }}
                                            sx={{
                                                height: listSize === "small" ? "32px" : "40px",
                                                "&:hover": {
                                                    backgroundColor: alpha(theme.palette.secondary[800], 0.08),
                                                },
                                                "&:active": {
                                                    backgroundColor: alpha("#c9d4f4", 0.04),
                                                },
                                                "&.Mui-selected, &.Mui-selected:hover": {
                                                    backgroundColor: alpha("#c9d4f4", 0.4),
                                                },
                                            }}
                                        >
                                            {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                                            {item.control && <>{item.control}</>}
                                            <EllipsisListItemText
                                                primary={item.label}
                                                tooltipPlacement={"bottom"}
                                                primaryTypographyProps={{
                                                    variant: "body1_400",
                                                    component: "span",
                                                    color: item.color ?? theme.palette.greySecondary.main,
                                                }}
                                            />
                                        </MenuItem>,
                                    ];
                                })}
                        </MenuList>
                        {actions && (
                            <div id={"menu-item-actions"} role={"menuitem"}>
                                {actions}
                            </div>
                        )}
                    </Popover>
                )}
            </>
        );
    },
);

export const ActionMenu = ({ list, ...restProps }) => {
    const { t } = useTranslation();
    return (
        <Menu
            open
            menuMinWidth={160}
            endIcon={
                <Box
                    sx={{
                        width: `24px`,
                        height: `24px`,
                        borderRadius: `100%`,
                        display: `flex`,
                        alignItems: `center`,
                        justifyContent: `center`,
                        marginRight: `-10px`,
                    }}
                >
                    <IconArrowDropDown size={20} />
                </Box>
            }
            endOpenIcon={
                <Box
                    sx={{
                        width: `24px`,
                        height: `24px`,
                        borderRadius: `100%`,
                        display: `flex`,
                        alignItems: `center`,
                        justifyContent: `center`,
                        marginRight: `-10px`,
                        backgroundColor: alpha(theme.palette.secondary.light, 0.08),
                    }}
                >
                    <IconArrowDropUp size={20} />
                </Box>
            }
            label={t("common::label::Actions")}
            size={"medium"}
            variant={"outlined"}
            color={"greySecondary"}
            placement={"right"}
            list={list}
            {...restProps}
        />
    );
};

export default Menu;
